import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import clsx from "clsx";
import { ApprovalStatusCheckbox } from "components/SmartSurveyor/ReviewPage/ApprovalStatusCheckbox";
import { Button } from "components/common/Button";
import { ExportSelectionRow } from "./ExportSelectionRow";
import type { ExportSelectionModalContentProps } from "./types";

export const ExportSelectionModalContent = ({
  onClose,
  onExport,
  items,
  initialSelectedLabels,
  isExporting,
}: ExportSelectionModalContentProps) => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelectedLabels),
  );

  const allSelected = items.length > 0 && selected.size === items.length;
  const noneSelected = selected.size === 0;

  const handleToggleRow = (label: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(items.map((item) => item.label)),
    );
  };

  const handleExport = async () => {
    const selectedLabels = items
      .filter((item) => selected.has(item.label))
      .map((item) => item.label);

    try {
      await onExport({ selectedLabels });
    } finally {
      onClose();
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">
          {t("smartSurveyor.exportSelection.title")}
        </h2>
        <p className="text-sm leading-relaxed text-gray-500">
          {t("smartSurveyor.exportSelection.description")}
        </p>
      </div>
      <div className="rounded-xl border border-blue-100 bg-white shadow-sm shadow-gray-200/50 overflow-hidden">
        <button
          type="button"
          onClick={handleToggleAll}
          className={clsx(
            "w-full flex items-center gap-3.5 px-4 py-3.5 border-b border-blue-700 bg-blue-600 text-left transition-colors duration-150",
            "hover:bg-blue-700",
          )}
        >
          <span className="flex-1 text-xs font-bold uppercase tracking-wider text-white">
            {t("smartSurveyor.exportSelection.all")}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white text-xs font-medium text-blue-700 tabular-nums">
            {selected.size} / {items.length}
          </span>
          <ApprovalStatusCheckbox
            checked={allSelected}
            onToggle={handleToggleAll}
          />
        </button>
        <ul className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100/70">
          {items.map((item, rowIndex) => (
            <ExportSelectionRow
              key={item.label}
              label={item.label}
              value={item.value}
              rgb={item.rgb}
              iconDataUrl={item.iconDataUrl}
              index={rowIndex + 1}
              checked={selected.has(item.label)}
              onToggle={() => handleToggleRow(item.label)}
            />
          ))}
        </ul>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button onClick={onClose} disabled={isExporting}>
          {t("smartSurveyor.common.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={noneSelected || isExporting}
          startAdornment={
            isExporting ? <Loader2 size={16} className="animate-spin" /> : null
          }
        >
          {t("smartSurveyor.exportSelection.export")}
        </Button>
      </div>
    </div>
  );
};
